import { Stack, IconLink } from "@ucchub/ui";
import { useTranslation } from "react-i18next";
import Container from "@vfuk/core-container";
import Paragraph from "@vfuk/core-paragraph/";
import Heading from "@vfuk/core-heading";
import DetailsRow from "../customer/DetailsRow";
import Spacing from "@vfuk/core-spacing";
import Divider from "@vfuk/core-divider";
import SimpleGrid from "@vfuk/core-simple-grid";
import Span from "@vfuk/core-span";
import type { OrganisationInfo } from "@components/accountConfiguration/AccountConfig.types";
import StateNotification from "@vfuk/core-state-notification";
import Icon from "@vfuk/core-icon";
import Link from "@vfuk/core-link";

type Props = {
  organisationInfo: OrganisationInfo;
  billingProfileCount?: number;
  destinationsCount?: number;
  onClickViewBillingProfiles: () => void;
  onClickViewDestinations: () => void;
  onClickEdit: () => void;
};

const AccountConfigView = ({
  organisationInfo,
  onClickViewBillingProfiles,
  billingProfileCount,
  destinationsCount,
  onClickEdit,
  onClickViewDestinations,
}: Props) => {
  const { t } = useTranslation();

  const isTomiaProvisioned = organisationInfo.tomiaProvisioned;
  const isDestinationsIconHidden = destinationsCount === 0;

  const isBillingProfileIconHidden = billingProfileCount === 0;

  const tomiaOnboardingBlockHeight = "70px";

  return (
    <Container appearance="primary">
      {!isTomiaProvisioned && (
        <Spacing spacingLevel={{ bottom: 3 }}>
          <StateNotification state="warn">
            <Paragraph>{t("core:accountConfig:tomia_warn")}</Paragraph>
          </StateNotification>
        </Spacing>
      )}
      <Stack justifyContent="space-between" align="baseline">
        <Heading level={4} weight={3}>
          {t("core:accountConfig:account_config")}
        </Heading>
        {isTomiaProvisioned && (
          <IconLink
            icon={{ justify: "left", name: "edit" }}
            text={t("core:customers:edit")}
            appearance="secondary"
            onClick={onClickEdit}
          />
        )}
      </Stack>

      <DetailsRow>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:accountConfig:commercial_manager_email")}
          </Paragraph>
          <Paragraph size={2} weight={2}>
            {organisationInfo.commercialManagerEmail}
          </Paragraph>
        </div>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:accountConfig:account_manager_email")}
          </Paragraph>
          <Paragraph size={2}>{organisationInfo.accountManagerEmail}</Paragraph>
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:accountConfig:voice_platform_id")}
          </Paragraph>
          <Paragraph size={2}>{organisationInfo.voicePlatformId}</Paragraph>
        </div>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:accountConfig:messaging_platform_id_view")}
          </Paragraph>
          <Paragraph size={2}>
            {organisationInfo.messagingPlatformId || "NA"}
          </Paragraph>
        </div>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:accountConfig:customer_commercial_id")}
          </Paragraph>
          <Paragraph size={2}>{organisationInfo.commercialId}</Paragraph>
        </div>
      </DetailsRow>
      <Spacing spacingLevel={{ top: 6, bottom: 4 }}>
        <Divider appearance="secondary" />
      </Spacing>
      <Heading level={4} weight={3}>
        {t("core:accountConfig:tomia_onboarding")}
      </Heading>
      <SimpleGrid
        columns={{
          sm: 3,
          lg: 3,
        }}
        spacing={3}
      >
        <Container appearance="primary" elevation>
          <Stack height={tomiaOnboardingBlockHeight} align="center">
            <Stack columnGap="10px">
              {!isTomiaProvisioned && (
                <Icon size={1} name="warning" group="midLight" />
              )}
              {isTomiaProvisioned && (
                <Icon size={1} name="completed" group="midLight" />
              )}
              <Heading level={5} weight={3}>
                {t("core:accountConfig:tomia_integrity")}
              </Heading>
            </Stack>
          </Stack>
        </Container>

        <Container appearance="primary" elevation>
          <Stack
            direction="column"
            align="start"
            rowGap="5px"
            height={tomiaOnboardingBlockHeight}
            justifyContent="center"
          >
            <Stack columnGap="10px">
              {isBillingProfileIconHidden && <Span weight={4}> ─ </Span>}
              {!!billingProfileCount && !isBillingProfileIconHidden && (
                <Icon size={1} name="bill" group="midLight" />
              )}
              <Heading level={5} weight={3}>
                {t("core:accountConfig:billing_profiles")}
              </Heading>
            </Stack>
            {!!billingProfileCount && !isBillingProfileIconHidden && (
              <Link showUnderline onClick={onClickViewBillingProfiles}>
                {t("core:accountConfig:view")}
              </Link>
            )}
          </Stack>
        </Container>
        <Container appearance="primary" elevation>
          <Stack
            direction="column"
            align="start"
            rowGap="5px"
            height={tomiaOnboardingBlockHeight}
            justifyContent="center"
          >
            <Stack columnGap="10px">
              {isDestinationsIconHidden && <Span weight={4}> ─ </Span>}
              {!!destinationsCount && !isDestinationsIconHidden && (
                <Icon size={1} name="bill" group="midLight" />
              )}
              <Heading level={5} weight={3}>
                {t("core:accountConfig:destinations")}
              </Heading>
            </Stack>
            {!!destinationsCount && !isDestinationsIconHidden && (
              <Link showUnderline onClick={onClickViewDestinations}>
                {t("core:accountConfig:view")}
              </Link>
            )}
          </Stack>
        </Container>
      </SimpleGrid>
      <Spacing spacingLevel={{ top: 6, bottom: 3 }}>
        <Divider appearance="secondary" />
      </Spacing>
    </Container>
  );
};

export default AccountConfigView;
